import useUrlState from "@ahooksjs/use-url-state";
import React, { useEffect, useMemo } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { Skeleton } from "primereact/skeleton";
import MeaPage from "../../components/MeaPage";
import { useAuthContext } from "../../hooks/useAuthContext";
import trainingsAPI from "../../services/trainings";
import teachersAPI from "../../services/teachers";
import sessionsAPI from "../../services/sessions";
import accountsAPI from "../../services/accounts";
import SessionsListComponent from "../trainings/sessions/components/SessionsListComponent";
import { ToFormatedShortISODate } from "../../utils/dates";

export function TeacherSessionsPage() {
  const { school } = useLoaderData();
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const { auth, currentMembership, updateCurrentMembershipPreferences } = useAuthContext();

  const trainingQuery = trainingsAPI.GetOne(school?.id, trainingId, auth?.access_token);
  const training = trainingQuery?.data?.data;
  const trainingsQuery = trainingsAPI.useGetList(
    school?.id,
    auth?.access_token,
    1,
    100,
    ""
  );
  const trainings = trainingsQuery?.data?.data || [];

  const teachersQuery = teachersAPI.useGetList(
    school?.id,
    trainingId,
    auth?.access_token,
    1,
    1000
  );
  const teacher = useMemo(
    () =>
      (teachersQuery?.data?.data || []).find(
        (item) =>
          item?.member_id === currentMembership?.id || item?.member?.id === currentMembership?.id
      ),
    [currentMembership?.id, teachersQuery?.data?.data]
  );

  const [state, setState] = useUrlState(
    {
      teacherSessionsMonth: "",
    },
    { navigateMode: "replace" }
  );

  const monthOptions = useMemo(() => {
    const startDate = training?.start_courses_date
      ? new Date(training.start_courses_date)
      : new Date();
    const endDate = training?.end_courses_date
      ? new Date(training.end_courses_date)
      : new Date();
    const months = [];
    const loopDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

    while (loopDate <= endDate) {
      const year = loopDate.getFullYear();
      const month = loopDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      months.push({
        label: firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
        value: `${year}-${String(month + 1).padStart(2, "0")}`,
        start_date: ToFormatedShortISODate(firstDay),
        end_date: ToFormatedShortISODate(lastDay),
      });

      loopDate.setMonth(loopDate.getMonth() + 1);
    }

    if (months.length === 0) {
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      months.push({
        label: firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
        value: `${year}-${String(month + 1).padStart(2, "0")}`,
        start_date: ToFormatedShortISODate(firstDay),
        end_date: ToFormatedShortISODate(lastDay),
      });
    }

    return months;
  }, [training?.end_courses_date, training?.start_courses_date]);

  const currentMonthValue = useMemo(() => {
    const currentDate = new Date();
    return `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const selectedMonth = state.teacherSessionsMonth || null;
  const selectedMonthData =
    monthOptions.find((month) => month.value === selectedMonth) || monthOptions[0];

  useEffect(() => {
    if (!monthOptions[0]) {
      return;
    }

    if (!selectedMonth) {
      const defaultMonth =
        monthOptions.find((month) => month.value === currentMonthValue) || monthOptions[0];
      setState((currentState) => ({
        ...currentState,
        teacherSessionsMonth: defaultMonth.value,
      }));
      return;
    }

    const selectedMonthStillAvailable = monthOptions.some(
      (month) => month.value === selectedMonth
    );
    if (!selectedMonthStillAvailable) {
      setState((currentState) => ({
        ...currentState,
        teacherSessionsMonth: monthOptions[0].value,
      }));
    }
  }, [currentMonthValue, monthOptions, selectedMonth, setState]);

  const monthlySessionsFilter = useMemo(
    () => ({
      teacher_id: teacher?.id,
      start_date: selectedMonthData?.start_date,
      end_date: selectedMonthData?.end_date,
    }),
    [selectedMonthData?.end_date, selectedMonthData?.start_date, teacher?.id]
  );

  const sessionsQuery = sessionsAPI.useGetList(
    school?.id,
    teacher?.id ? trainingId : "",
    auth?.access_token,
    1,
    1000,
    monthlySessionsFilter
  );

  const plannedSessionsCount = (sessionsQuery?.data?.data || []).length;
  const effectiveSessionsCount = useMemo(
    () =>
      (sessionsQuery?.data?.data || []).filter(
        (session) => session?.attendances && session.attendances.length > 0
      ).length,
    [sessionsQuery?.data?.data]
  );

  const effectivePointsTotal = useMemo(
    () =>
      (sessionsQuery?.data?.data || []).reduce((total, session) => {
        if (!session?.attendances || session.attendances.length === 0) {
          return total;
        }
        return total + (Number(session?.points) || 0);
      }, 0),
    [sessionsQuery?.data?.data]
  );

  const isLoadingPage = trainingQuery.isLoading || teachersQuery.isLoading;

  const getStatusLabel = (status) => {
    switch (status) {
      case "OPEN":
        return "Ouvert";
      case "DRAFT":
        return "Brouillon";
      case "SUSPENDED":
        return "Suspendu";
      case "ARCHIVED":
        return "Archivé";
      default:
        return status;
    }
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <span className="font-medium text-color">{option.name}</span>
        <span
          className={`text-xs px-2 py-1 border-round font-semibold ml-2 ${
            option.status === "OPEN"
              ? "bg-green-100 text-green-800"
              : option.status === "DRAFT"
              ? "bg-blue-100 text-blue-800"
              : option.status === "SUSPENDED"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const valueTemplate = (option, props) => {
    if (!option) {
      const currentReg = trainings.find((t) => t.id === trainingId) || training;
      if (!currentReg) {
        return <span>{props.placeholder}</span>;
      }
      option = currentReg;
    }
    return (
      <div className="flex align-items-center">
        <span className="font-bold text-color mr-2">{option.name}</span>
        <span
          className={`text-xs px-2 py-0.5 border-round font-semibold ${
            option.status === "OPEN"
              ? "bg-green-100 text-green-800"
              : option.status === "DRAFT"
              ? "bg-blue-100 text-blue-800"
              : option.status === "SUSPENDED"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const handleTrainingChange = async (e) => {
    const newTrainingId = e.value;
    if (newTrainingId && newTrainingId !== trainingId) {
      try {
        const updatedPrefs = await accountsAPI.UpdateMemberPreferences(
          auth?.access_token,
          currentMembership?.id,
          newTrainingId
        );
        if (updatedPrefs) {
          updateCurrentMembershipPreferences(updatedPrefs);
        } else {
          const fallbackPrefs = currentMembership?.preferences
            ? { ...currentMembership.preferences, last_visited_training: newTrainingId }
            : { last_visited_training: newTrainingId };
          updateCurrentMembershipPreferences(fallbackPrefs);
        }
      } catch (err) {
        console.error("Failed to update last visited preference", err);
      }
      navigate(`/school/${school?.id}/teacher/sessions/${newTrainingId}`);
    }
  };

  const sectionOptions = [
    { label: "Mes séances de cours", value: "sessions" },
    { label: "Mes classes", value: "classes" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Agenda", value: "agenda" },
  ];

  const handleSectionChange = (e) => {
    const newSection = e.value;
    if (!newSection || newSection === "sessions") {
      return;
    }

    if (newSection === "classes") {
      navigate(`/school/${school?.id}/teacher/classes/${trainingId}`);
      return;
    }

    if (newSection === "agenda") {
      navigate(`/school/${school?.id}/teacher/agenda/${trainingId}`);
      return;
    }

    navigate(`/school/${school?.id}/teacher/avis-passage/${trainingId}`);
  };

  const customWorkspaceBar = (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value="sessions"
          options={sectionOptions}
          onChange={handleSectionChange}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={trainingId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={handleTrainingChange}
          placeholder={training?.name || "Sélectionner une formation"}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
          itemTemplate={itemTemplate}
          valueTemplate={valueTemplate}
        />
      </div>
    </div>
  );

  return (
    <MeaPage
      showBackButton={true}
      onBackButtonClick={() => navigate(`/school/${school?.id}/teacher`)}
      title="Mes séances de cours"
      breadcrumb={customWorkspaceBar}
    >
      <div className="px-4 md:px-6 lg:px-8 py-3">
        {isLoadingPage && (
          <>
            <div className="grid mb-3">
              <div className="col-12 md:col-4">
                <Skeleton height="7rem" className="border-round" />
              </div>
              <div className="col-12 md:col-4">
                <Skeleton height="7rem" className="border-round" />
              </div>
              <div className="col-12 md:col-4">
                <Skeleton height="7rem" className="border-round" />
              </div>
            </div>
            <Skeleton height="3rem" className="mb-3" />
            <Skeleton height="18rem" className="border-round" />
          </>
        )}

        {!isLoadingPage && !teacher && (
          <div className="text-center my-6">
            <i className="pi pi-info-circle text-400 text-5rem mb-3"></i>
           
            <div className="text-600 text-sm">
              Aucune séance de cours trouvée
            </div>
          </div>
        )}

        {!isLoadingPage && teacher && (
          <>
            <div className="grid mb-3">
              <div className="col-12 md:col-4">
                <div className="surface-card shadow-1 border-round p-3">
                  <div className="text-500 text-sm">Séances prévues du mois</div>
                  <div className="text-3xl font-semibold mt-2">{plannedSessionsCount}</div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="surface-card shadow-1 border-round p-3">
                  <div className="text-500 text-sm">Séances effectives du mois</div>
                  <div className="text-3xl font-semibold mt-2">{effectiveSessionsCount}</div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="surface-card shadow-1 border-round p-3">
                  <div className="text-500 text-sm">Total des points du mois</div>
                  <div className="text-3xl font-semibold mt-2">
                    {new Intl.NumberFormat("fr-FR", {
                      maximumFractionDigits: 2,
                    }).format(effectivePointsTotal)}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <SelectButton
                value={selectedMonth || selectedMonthData?.value}
                options={monthOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) =>
                  setState((currentState) => ({
                    ...currentState,
                    teacherSessionsMonth: e.value || "",
                  }))
                }
                className="w-full flex-wrap"
              />
            </div>

            <SessionsListComponent
              key={`${trainingId}-${teacher.id}-${selectedMonthData?.value || "all"}`}
              schoolId={school?.id}
              trainingId={trainingId}
              currentTeacherId={teacher.id}
              disableNavigation={true}
              filter={monthlySessionsFilter}
              showAddButton={false}
              showFilterButton={false}
              showAllSessions={true}
              selectedFields={{
                presence: true,
                replacement: true,
                teacher: false,
                edit: false,
              }}
            />
          </>
        )}
      </div>
    </MeaPage>
  );
}

export default TeacherSessionsPage;
